// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Sparklight — the rules. Pure, synchronous, and the single source of truth for the game, the bot,
// the simulation and the audit's positive controls.
//
// ── THE ONE IDEA THAT MAKES THIS SYNCHRONISE ─────────────────────────────────────────────────────
//
// Every other game in this fleet hides cards FROM the other players. This one hides them from YOU,
// and that inversion is what dictates the whole design.
//
// A hand is not an array of cards. It is an array of DECK POSITIONS, and positions are completely
// public: everybody knows that seat 2's third card is deck position 17. What varies per peer is the
// lookup table `seen[]`, which maps a position to the card sitting there — and which is masked to
// HIDDEN for exactly the positions in your own hand, and truncated at the positions the deck has not
// dealt yet. So the state machine below is identical on every peer, byte for byte, and the only
// difference between the host's view and yours is a handful of zeroes.
//
// ── AND THE ONE PLACE THAT WOULD OTHERWISE DESYNC ────────────────────────────────────────────────
//
// A hint touches a SUBSET of the target's slots, and which subset depends on cards the target is not
// allowed to see. That is a control-flow branch taken on a hidden hand, which is precisely how a
// room desynchronises: the host would compute "slots 1 and 3", the target would compute nothing at
// all, and from that ply on the two of them are playing different games.
//
// So the touched-slot mask is packed INTO THE ACTION and travels on the wire. Nobody derives it.
// `hintMask()` computes it once, on a peer that can actually see the hand, and `apply()` trusts what
// it is given — while `tests/audit.ts` independently re-derives every mask from the true deal and
// holds disagreements at zero. Deriving is what would break; asserting is what catches it.

import { HIDDEN, TOP, handSize, rankOf, suitOf, touchedByColour, type SuitSpec } from './cards';
import { suitsOf, type Mode } from './modes';

// Re-exported so a consumer never has to reach past the rules to name "a card you may not see".
export { HIDDEN };

/** Sparks in hand at the start, and the ceiling a returned spark can never push past. */
export const MAX_SPARKS = 8;
/** Failed raises the sky can absorb. The third one ends the night. */
export const MAX_HAZE = 3;

// ── actions ───────────────────────────────────────────────────────────────────────────────────────

export const A_RAISE = 0;
export const A_DOUSE = 1;
export const A_POINT = 2;

/** Hint kinds. Packed as one bit, so these two are the only values that will ever exist. */
export const K_COLOUR = 0;
export const K_RANK = 1;

export const packRaise = (seat: number, slot: number): number => A_RAISE | (seat << 2) | (slot << 5);
export const packDouse = (seat: number, slot: number): number => A_DOUSE | (seat << 2) | (slot << 5);
export const packPoint = (seat: number, target: number, kind: number, value: number, mask: number): number =>
  A_POINT | (seat << 2) | (target << 5) | (kind << 8) | (value << 9) | (mask << 12);

export const actType = (a: number): number => a & 3;
export const actSeat = (a: number): number => (a >> 2) & 7;
export const actSlot = (a: number): number => (a >> 5) & 7;
export const actTarget = (a: number): number => (a >> 5) & 7;
export const actKind = (a: number): number => (a >> 8) & 1;
export const actValue = (a: number): number => (a >> 9) & 7;
export const actMask = (a: number): number => (a >> 12) & 31;

// ── events ────────────────────────────────────────────────────────────────────────────────────────

export type Ev =
  | { k: 'point'; seat: number; target: number; kind: number; value: number; slots: number[] }
  | { k: 'raise'; seat: number; slot: number; card: number; ok: boolean; height: number }
  | { k: 'douse'; seat: number; slot: number; card: number }
  | { k: 'draw'; seat: number; pos: number }
  /**
   * `haze` and `sparks` are reported here rather than left for a reader to re-derive, and that is
   * deliberate: it gives the audit something INDEPENDENT to compare its own reconstruction against.
   * Without them the audit's haze check compared a variable with itself and could never fire.
   */
  | { k: 'end'; reason: EndReason; score: number; haze: number; sparks: number };

/**
 * `alone` is not a rules ending — it is the room collapsing to one person. It exists so that a
 * survivor reaches the summary instead of a board that will never move again.
 */
export type EndReason = 'haze' | 'perfect' | 'deck' | 'alone';

// ── knowledge ─────────────────────────────────────────────────────────────────────────────────────

/**
 * What its holder has been TOLD about one card. Bitmasks over suit ids and ranks, so a card can carry
 * two positive colours at once — which in Prism mode is not a contradiction but a proof.
 */
export interface Known {
  /** Colours this card has been positively pointed at as. */
  yes: number;
  /** Colours a hint went past it for, so it is provably not (and, in Prism, provably not Prism). */
  no: number;
  /** Ranks positively pointed at. Only ever one bit in practice, but a set is cheaper than a guard. */
  rankYes: number;
  rankNo: number;
}

export const emptyKnown = (): Known => ({ yes: 0, no: 0, rankYes: 0, rankNo: 0 });
export const bit = (n: number): number => 1 << n;
export const hasBit = (mask: number, n: number): boolean => (mask & (1 << n)) !== 0;
/** Whether this card has been touched by any hint at all — the "clued" flag every convention needs. */
export const isTouched = (k: Known): boolean => k.yes !== 0 || k.rankYes !== 0;

// ── state ─────────────────────────────────────────────────────────────────────────────────────────

export interface Setup {
  mode: Mode;
  players: number;
  hand: number;
  /** The starting seat, derived from the shared seed so every peer agrees without being told. */
  first: number;
}

export interface Game {
  readonly setup: Setup;
  readonly suits: SuitSpec[];
  /**
   * Position -> card, for every position the deck has dealt. Entries are HIDDEN where this peer is
   * not allowed to look. Length grows as cards are drawn; it is never the whole deck, because a peer
   * holding the undealt order would know the future.
   */
  seen: number[];
  /** Total cards in the deck for this mode. Public: everyone can count. */
  deckSize: number;
  /** How many positions have left the deck. `deckSize - drawn` is the draw pile. */
  drawn: number;
  /** Deck positions, per seat, oldest first. The newest card is always the last element. */
  hands: number[][];
  /** Per-seat, per-slot knowledge. Moves with the card when a slot is spliced out. */
  known: Known[][];
  /** Height of each plume, indexed by suit id. */
  plumes: number[];
  /** Every card that has been doused or has failed a raise, in order. */
  ash: number[];
  sparks: number;
  haze: number;
  turn: number;
  turnNo: number;
  /** Set when the draw pile empties: the last turn index that will be played. -1 while stocked. */
  endsAfter: number;
  over: boolean;
  reason: EndReason | null;
  log: number[];
  events: Ev[];
}

/**
 * Deal from a known deck order. The host does this; a guest is handed the result.
 *
 * `deckSize` is separate from `order.length` on purpose. A guest's `order` is its masked `seen`
 * table, which only ever reaches as far as the cards that have actually been dealt — so taking the
 * deck size from its length would tell the guest the pile ran dry the moment the deal finished, and
 * `endsAfter` would fire on turn zero. The size of the deck is public; its contents are not.
 */
export function createGame(setup: Setup, order: number[], deckSize = order.length): Game {
  const suits = suitsOf(setup.mode);
  const g: Game = {
    setup,
    suits,
    seen: [],
    deckSize,
    drawn: 0,
    hands: Array.from({ length: setup.players }, () => []),
    known: Array.from({ length: setup.players }, () => []),
    plumes: Array.from({ length: 6 }, () => 0),
    ash: [],
    sparks: MAX_SPARKS,
    haze: 0,
    turn: setup.first,
    turnNo: 0,
    endsAfter: -1,
    over: false,
    reason: null,
    log: [],
    events: [],
  };
  // Deal round-robin from the first seat, exactly as a person would.
  for (let n = 0; n < setup.hand; n++) {
    for (let i = 0; i < setup.players; i++) {
      const seat = (setup.first + i) % setup.players;
      draw(g, seat, order);
    }
  }
  return g;
}

/**
 * An empty game with no cards dealt, for a peer that has been handed `seen` and a log rather than a
 * deck. `replay` fills it in; `order` is the masked table, so the guest deals itself blanks.
 */
export const createFrom = (setup: Setup, seen: number[], deckSize: number): Game =>
  createGame(setup, seen, deckSize);

function draw(g: Game, seat: number, order: number[]): void {
  if (g.drawn >= g.deckSize) return;
  const pos = g.drawn++;
  g.seen[pos] = order[pos] ?? HIDDEN;
  g.hands[seat].push(pos);
  g.known[seat].push(emptyKnown());
  g.events.push({ k: 'draw', seat, pos });
  // The pile is empty: play goes once more round the table and stops just short of coming back to
  // whoever took the last card.
  if (g.drawn >= g.deckSize && g.endsAfter < 0) g.endsAfter = g.turnNo + g.setup.players - 1;
}

export const cardAt = (g: Game, pos: number): number => g.seen[pos] ?? HIDDEN;
/** The cards in a seat's hand, as far as this peer may look. HIDDEN for your own. */
export const handCards = (g: Game, seat: number): number[] => g.hands[seat].map((p) => cardAt(g, p));

export const score = (g: Game): number => g.plumes.reduce((a, b) => a + b, 0);
export const perfect = (g: Game): boolean => g.suits.every((s) => g.plumes[s.id] >= TOP);

/** Is this card the next one its plume wants? */
export const raisable = (g: Game, c: number): boolean =>
  c !== HIDDEN && g.plumes[suitOf(c)] === rankOf(c) - 1 && g.suits.some((s) => s.id === suitOf(c));

// ── hints ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * Which of `target`'s slots a hint would touch, as a bitmask. Requires a view that can actually SEE
 * the target's hand — which is why the result is packed into the action rather than recomputed by
 * everyone. Returns 0 for a hint that touches nothing, which is illegal.
 */
export function hintMask(g: Game, target: number, kind: number, value: number): number {
  let mask = 0;
  const hand = g.hands[target];
  for (let i = 0; i < hand.length; i++) {
    const c = cardAt(g, hand[i]);
    if (c === HIDDEN) continue;
    const hit = kind === K_COLOUR ? touchedByColour(c, value, g.suits) : rankOf(c) === value;
    if (hit) mask |= 1 << i;
  }
  return mask;
}

/** Every hint that touches at least one card, as packed actions ready to submit. */
export function legalHints(g: Game, seat: number): number[] {
  const out: number[] = [];
  if (g.sparks <= 0) return out;
  for (let t = 0; t < g.setup.players; t++) {
    if (t === seat) continue;
    for (const s of g.suits) {
      const m = hintMask(g, t, K_COLOUR, s.id);
      if (m !== 0) out.push(packPoint(seat, t, K_COLOUR, s.id, m));
    }
    for (let r = 1; r <= TOP; r++) {
      const m = hintMask(g, t, K_RANK, r);
      if (m !== 0) out.push(packPoint(seat, t, K_RANK, r, m));
    }
  }
  return out;
}

// ── legality ──────────────────────────────────────────────────────────────────────────────────────

export function legal(g: Game, a: number): boolean {
  if (g.over) return false;
  const seat = actSeat(a);
  if (seat !== g.turn || seat >= g.setup.players) return false;
  const t = actType(a);

  if (t === A_RAISE || t === A_DOUSE) {
    const slot = actSlot(a);
    if (slot >= g.hands[seat].length) return false;
    // Dousing at a full spark pool would burn a card for nothing, so the rules forbid it outright —
    // and a crew that has run out of anything to say still has raising, always.
    if (t === A_DOUSE && g.sparks >= MAX_SPARKS) return false;
    return true;
  }

  if (t === A_POINT) {
    if (g.sparks <= 0) return false;
    const target = actTarget(a);
    if (target === seat || target >= g.setup.players) return false;
    const kind = actKind(a);
    const value = actValue(a);
    if (kind === K_RANK && (value < 1 || value > TOP)) return false;
    if (kind === K_COLOUR && !g.suits.some((s) => s.id === value)) return false;
    // A hint that touches nothing is not a hint, it is a wasted spark and a lie about the hand.
    if (actMask(a) === 0) return false;
    // Only ever verified where the hand is visible. A guest replaying the host's log has to take the
    // mask on trust — that is the whole point of putting it on the wire — but the host, the solo
    // game and the simulation all check it, which is every place it could be wrong.
    const mask = hintMask(g, target, kind, value);
    const anyHidden = g.hands[target].some((p) => cardAt(g, p) === HIDDEN);
    if (!anyHidden && mask !== actMask(a)) return false;
    return true;
  }
  return false;
}

// ── applying ──────────────────────────────────────────────────────────────────────────────────────

/**
 * Advance the game by one action. `order` supplies the replacement card when the actor draws; a
 * guest passes its masked table and gets HIDDEN, which is exactly right.
 */
export function apply(g: Game, a: number, order: number[]): boolean {
  if (!legal(g, a)) return false;
  const seat = actSeat(a);
  const t = actType(a);

  if (t === A_POINT) {
    const target = actTarget(a);
    const kind = actKind(a);
    const value = actValue(a);
    const mask = actMask(a);
    const slots: number[] = [];
    const hand = g.hands[target];
    for (let i = 0; i < hand.length; i++) {
      const k = g.known[target][i];
      const touched = (mask & (1 << i)) !== 0;
      if (touched) slots.push(i);
      if (kind === K_COLOUR) {
        if (touched) k.yes |= bit(value);
        else k.no |= bit(value);
      } else if (touched) k.rankYes |= bit(value);
      else k.rankNo |= bit(value);
    }
    g.sparks--;
    g.events.push({ k: 'point', seat, target, kind, value, slots });
    g.log.push(a);
    return step(g);
  }

  const slot = actSlot(a);
  const pos = g.hands[seat][slot];
  const c = cardAt(g, pos);

  if (t === A_RAISE) {
    const ok = raisable(g, c);
    if (ok) {
      g.plumes[suitOf(c)] = rankOf(c);
      // A finished plume hands a spark back to the crew — the game's only renewable reward for
      // getting something completely right.
      if (rankOf(c) === TOP && g.sparks < MAX_SPARKS) g.sparks++;
    } else {
      g.ash.push(c);
      g.haze++;
    }
    g.events.push({ k: 'raise', seat, slot, card: c, ok, height: g.plumes[suitOf(c)] ?? 0 });
  } else {
    g.ash.push(c);
    if (g.sparks < MAX_SPARKS) g.sparks++;
    g.events.push({ k: 'douse', seat, slot, card: c });
  }

  g.hands[seat].splice(slot, 1);
  g.known[seat].splice(slot, 1);
  draw(g, seat, order);
  g.log.push(a);
  return step(g);
}

function step(g: Game): boolean {
  if (g.haze >= MAX_HAZE) return finish(g, 'haze');
  if (perfect(g)) return finish(g, 'perfect');
  if (g.endsAfter >= 0 && g.turnNo >= g.endsAfter) return finish(g, 'deck');
  g.turnNo++;
  g.turn = (g.turn + 1) % g.setup.players;
  return true;
}

/**
 * End the night from outside the rules, with whatever the crew had raised.
 *
 * The one caller is a room that has emptied out. A co-op has no winner to hand it to, so there is
 * nothing to adjudicate — but a night that simply stops is the worst outcome available, and it is
 * precisely what happened before this existed: the last player sat on a correct, frozen board where
 * it was nobody's turn, for ever.
 */
export function concede(g: Game, reason: EndReason): boolean {
  if (g.over) return false;
  return finish(g, reason);
}

function finish(g: Game, reason: EndReason): boolean {
  g.over = true;
  g.reason = reason;
  g.events.push({ k: 'end', reason, score: score(g), haze: g.haze, sparks: g.sparks });
  return true;
}

/**
 * Rebuild a game from its opening setup and the whole log. Returns false if any action in the log is
 * illegal for us, which is the signal that our `seen` table disagrees with the host's.
 *
 * Replaying the WHOLE log on every message, rather than applying a delta, is what makes a dropped or
 * duplicated broadcast a non-event.
 */
export function replay(setup: Setup, seen: number[], deckSize: number, log: number[]): Game | null {
  const g = createFrom(setup, seen, deckSize);
  for (const a of log) {
    if (!apply(g, a, seen)) return null;
  }
  return g;
}

/** Every action this seat could legally take right now. Used by the bot and by the legality tests. */
export function legalActions(g: Game, seat: number): number[] {
  const out: number[] = [];
  if (g.over || g.turn !== seat) return out;
  for (let i = 0; i < g.hands[seat].length; i++) {
    out.push(packRaise(seat, i));
    if (g.sparks < MAX_SPARKS) out.push(packDouse(seat, i));
  }
  return [...out, ...legalHints(g, seat)];
}

/** Cards of this rank and suit still unaccounted for — the copies-left count every save hint needs. */
export function copiesLeft(g: Game, c: number, spec: SuitSpec, seatsVisible: number[]): number {
  let left = spec.singleton ? 1 : [0, 3, 2, 2, 2, 1][rankOf(c)];
  for (const x of g.ash) if (x === c) left--;
  if (g.plumes[suitOf(c)] >= rankOf(c)) left = 0;
  void seatsVisible;
  return Math.max(0, left);
}

export const defaultSetup = (mode: Mode, players: number, seed: number): Setup => ({
  mode,
  players,
  hand: handSize(players),
  first: ((seed % players) + players) % players,
});
